import { useEffect } from 'react';

import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import { useAvailableLocations } from './useAvailableLocations';

/**
 * The organisation's sole "club" (location) when there is exactly one, else
 * undefined. Pure — extracted for testability (MYS-66).
 */
export function resolveSoleClub(
  locations?: OrganizationLocationDto[],
): OrganizationLocationDto | undefined {
  return locations && locations.length === 1 ? locations[0] : undefined;
}

type SingleClubState = {
  /** True when the org has exactly one club (so a selector is pointless). */
  isSingleClub: boolean;
  /** The sole club when `isSingleClub`, else undefined. */
  soleLocation?: OrganizationLocationDto;
  /** All available clubs (passthrough from the locations query). */
  locations?: OrganizationLocationDto[];
  isLoading: boolean;
};

/**
 * Read-only single-club detection. Use when the surrounding form already
 * defaults its own location state (e.g. product forms keyed on `locationId`)
 * and only needs to know whether to hide the selector.
 */
export function useIsSingleClub(): SingleClubState {
  // `isInitialLoading`, not the raw `isLoading` (D62). The underlying
  // `useGetLocations` is `enabled: Boolean(organizationId)`, and React Query
  // v4 reports a DISABLED query as `isLoading: true` / `fetchStatus: 'idle'`
  // — measured against the real library, not read from the docs — so the raw
  // flag never clears while no organization is selected. This hook's one
  // `isLoading` consumer is `LocationPicker`, which returns a bare `<Spinner/>`
  // from the navbar of every admin page, so the raw flag there is a permanent
  // navbar spinner. `isInitialLoading` is `isLoading && isFetching`: false for
  // a disabled query, true only while a request is really in flight.
  const { data: locations, isInitialLoading } = useAvailableLocations();
  const soleLocation = resolveSoleClub(locations);
  return {
    isSingleClub: Boolean(soleLocation),
    soleLocation,
    locations,
    isLoading: isInitialLoading,
  };
}

/**
 * MYS-66: when the org has a single club there is nothing to choose, so the
 * club selector should be hidden and the sole club auto-selected in the
 * background.
 *
 * Auto-selects the sole club whenever it isn't already the current selection.
 * The `selectedLocation?.id !== soleLocation.id` guard makes the effect
 * idempotent, so passing an inline `onSelect` (new identity every render)
 * cannot cause an update loop.
 */
export function useSingleClubAutoSelect({
  selectedLocation,
  onSelect,
}: {
  selectedLocation?: OrganizationLocationDto;
  onSelect: (location: OrganizationLocationDto) => void;
}): SingleClubState {
  const state = useIsSingleClub();
  const { soleLocation } = state;

  useEffect(() => {
    if (soleLocation && selectedLocation?.id !== soleLocation.id) {
      onSelect(soleLocation);
    }
  }, [soleLocation, selectedLocation, onSelect]);

  return state;
}
